import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useDataStale, useStaleFlags } from "@/contexts/DataStaleContext";
import { useGlobalAnalysisQuery } from "@/hooks/useGlobalAnalysis";
import { useVision360Query } from "@/hooks/useVision360";

interface DataStaleBannerProps {
  patientId: string;
  type: "vision360" | "globalAnalysis" | "both";
  onUpdate: () => void | Promise<void>;
  isUpdating?: boolean;
}

export function DataStaleBanner({ 
  patientId, 
  type, 
  onUpdate, 
  isUpdating = false 
}: DataStaleBannerProps) {
  const { 
    isVision360Stale, 
    isGlobalAnalysisStale, 
    clearVision360Stale,
    clearGlobalAnalysisStale,
    clearStale 
  } = useDataStale();
  const { data: staleFlags } = useStaleFlags(patientId);
  
  // Verificar se já existem análises geradas
  const { data: globalAnalysis } = useGlobalAnalysisQuery(patientId);
  const { data: vision360 } = useVision360Query(patientId);

  const vision360Stale = staleFlags?.vision360_stale_at != null || isVision360Stale(patientId);
  const globalAnalysisStale = staleFlags?.global_analysis_stale_at != null || isGlobalAnalysisStale(patientId);

  // Só mostrar o banner se:
  // 1. Os dados estão stale E
  // 2. A análise correspondente já foi gerada anteriormente
  const shouldShowVision360 = vision360Stale && vision360 !== null && vision360 !== undefined;
  const shouldShowGlobalAnalysis = globalAnalysisStale && globalAnalysis !== null && globalAnalysis !== undefined;

  const shouldShow =
    (type === "vision360" && shouldShowVision360) ||
    (type === "globalAnalysis" && shouldShowGlobalAnalysis) ||
    (type === "both" && (shouldShowVision360 || shouldShowGlobalAnalysis));

  if (!shouldShow) {
    return null;
  }

  const getMessage = () => {
    if (type === "vision360") {
      return "Houveram mudanças na timeline do paciente e as informações exibidas podem estar desatualizadas. Deseja atualizar a Visão 360?";
    }
    if (type === "globalAnalysis") {
      return "Houveram mudanças na timeline do paciente e as informações exibidas podem estar desatualizadas. Deseja atualizar a Análise Global Inteligente?";
    }
    // both
    if (shouldShowVision360 && shouldShowGlobalAnalysis) {
      return "Houveram mudanças na timeline do paciente e as informações exibidas podem estar desatualizadas. Deseja atualizar a Visão 360 e a Análise Global Inteligente?";
    }
    if (shouldShowVision360) {
      return "Houveram mudanças na timeline do paciente e as informações exibidas podem estar desatualizadas. Deseja atualizar a Visão 360?";
    }
    return "Houveram mudanças na timeline do paciente e as informações exibidas podem estar desatualizadas. Deseja atualizar a Análise Global Inteligente?";
  };

  const handleUpdate = async () => {
    try {
      await onUpdate();
      // Limpar apenas o flag específico após atualização bem-sucedida
      if (type === "vision360") {
        await clearVision360Stale(patientId);
      } else if (type === "globalAnalysis") {
        await clearGlobalAnalysisStale(patientId);
      } else if (type === "both") {
        // Se ambos estão stale, limpar apenas o que foi atualizado
        // Como não sabemos qual foi atualizado, vamos limpar ambos
        // Mas na prática, o onUpdate já deve ter limpo o específico
        // Então vamos limpar apenas o que está stale
        if (shouldShowVision360 && shouldShowGlobalAnalysis) {
          // Se ambos estão stale, limpar ambos (caso raro quando ambos são atualizados)
          await clearStale(patientId);
        } else if (shouldShowVision360) {
          await clearVision360Stale(patientId);
        } else if (shouldShowGlobalAnalysis) {
          await clearGlobalAnalysisStale(patientId);
        }
      }
    } catch (error) {
      // Se houver erro, não limpar o estado para que o banner continue visível
      console.error("Erro ao atualizar:", error);
    }
  };

  return (
    <Alert className="mb-4 border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-900">
      <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
      <AlertTitle className="text-orange-900 dark:text-orange-100">
        Dados precisam de atualização
      </AlertTitle>
      <AlertDescription className="text-orange-800 dark:text-orange-200 mt-2">
        <div className="flex items-center justify-between gap-4">
          <span>{getMessage()}</span>
          <Button
            size="sm"
            onClick={handleUpdate}
            disabled={isUpdating}
            className="bg-orange-600 hover:bg-orange-700 text-white shrink-0"
          >
            {isUpdating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Atualizando...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar
              </>
            )}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}

